import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { ConfirmModal } from '@/components/ConfirmModal';

import moment from 'moment';
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const scrollToLastMobileCard = _wg('scrollToLastMobileCard');

interface NPSDAttachmentsProps {
  attachmentList: any[];
  isEditMode?: boolean;
  theme?: string;
  showProfilePicOption?: boolean;
  profilePicId?: number | string | null;
  title?: string;
  onAdd?: () => void;
  onRemove?: (attachment: any) => void;
  onSetProfilePic?: (attachmentId: number) => void;
}

export function NPSDAttachments({
  attachmentList = [],
  isEditMode = false,
  theme: themeProp,
  showProfilePicOption = false,
  profilePicId = null,
  title = 'Attachments',
  onAdd,
  onRemove,
  onSetProfilePic,
}: NPSDAttachmentsProps) {
  const { theme: globalTheme } = useTheme();
  const resolvedTheme = themeProp ?? globalTheme;
  const rootRef = useRef<HTMLDivElement>(null);
  const prevLengthRef = useRef(attachmentList?.length || 0);

  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; attachment: any }>({
    open: false,
    attachment: null,
  });

  // Watch attachmentList length for scroll-to-new behavior
  useEffect(() => {
    const newLength = attachmentList?.length || 0;
    if (newLength > prevLengthRef.current) {
      if (rootRef.current) {
        scrollToLastMobileCard(rootRef.current);
      }
    }
    prevLengthRef.current = newLength;
  }, [attachmentList]);

  const attachmentCount = useMemo((): number => {
    return attachmentList?.length || 0;
  }, [attachmentList]);

  const hasImageExtension = useCallback((filename: string): boolean => {
    if (!filename) return false;
    const ext = filename.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].indexOf(ext || '') >= 0;
  }, []);

  const getFileIcon = useCallback((attachment: any): string => {
    const filename = attachment.filename || '';
    const ext = filename.toLowerCase().split('.').pop();
    if (['pdf'].indexOf(ext) !== -1) return 'fa-file-pdf';
    if (['doc', 'docx'].indexOf(ext) !== -1) return 'fa-file-word';
    if (['xls', 'xlsx'].indexOf(ext) !== -1) return 'fa-file-excel';
    if (['ppt', 'pptx'].indexOf(ext) !== -1) return 'fa-file-powerpoint';
    if (['zip', 'rar', '7z'].indexOf(ext) !== -1) return 'fa-file-archive';
    if (['txt'].indexOf(ext) !== -1) return 'fa-file-alt';
    if (['mp4', 'avi', 'mov', 'wmv'].indexOf(ext) !== -1) return 'fa-file-video';
    if (['mp3', 'wav', 'ogg'].indexOf(ext) !== -1) return 'fa-file-audio';
    return 'fa-file';
  }, []);

  const openAttachmentUrl = useCallback((attachment: any) => {
    if (attachment.signedURL) {
      window.open(attachment.signedURL, '_blank');
    }
  }, []);

  const formatDate = useCallback((date: any): string => {
    if (!date) return '';
    return typeof moment !== 'undefined' ? moment(date).format('MM/DD/YYYY') : '';
  }, []);

  const addAttachment = useCallback(() => {
    onAdd?.();
  }, [onAdd]);

  const openDeleteConfirm = useCallback((attachment: any) => {
    setDeleteConfirm({ open: true, attachment });
  }, []);

  const confirmDelete = useCallback(() => {
    if (deleteConfirm.attachment) {
      onRemove?.(deleteConfirm.attachment);
    }
    setDeleteConfirm({ open: false, attachment: null });
  }, [deleteConfirm.attachment, onRemove]);

  const cancelDelete = useCallback(() => {
    setDeleteConfirm({ open: false, attachment: null });
  }, []);

  const setProfilePic = useCallback((attachmentId: number) => {
    onSetProfilePic?.(attachmentId);
  }, [onSetProfilePic]);

  const isProfilePic = useCallback((attachment: any): boolean => {
    if (!profilePicId) return false;
    return profilePicId?.toString() === attachment.id?.toString();
  }, [profilePicId]);

  return (
    <div className={`npsd-attachments ${resolvedTheme}`} ref={rootRef}>
      {/* Header */}
      <div className="header">
        <h1>{title}</h1>
        {attachmentCount > 0 && <span className="badge">{attachmentCount}</span>}
        {isEditMode && (
          <button
            className="btn-add"
            onClick={addAttachment}
            title="Add Attachment"
          >
            <i className="fas fa-plus"></i>
            ATTACHMENT
          </button>
        )}
      </div>

      {/* Desktop Table */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th style={{ width: '80px' }}>Preview</th>
              <th style={{ width: '25%' }}>Title</th>
              <th style={{ width: '20%' }}>Filename</th>
              <th style={{ width: '25%' }}>Created</th>
              <th style={{ width: '120px' }}>Actions</th>
            </tr>
          </thead>
          {attachmentCount === 0 ? (
            <tbody>
              <tr>
                <td colSpan={5} className="empty-cell">
                  <i className="fas fa-paperclip"></i>
                  No attachments found
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {attachmentList.map((attachment) => (
                <tr key={'desktop-' + attachment.id}>
                  <td>
                    <div className="preview-container" onClick={() => openAttachmentUrl(attachment)}>
                      {hasImageExtension(attachment.filename) ? (
                        <img
                          src={attachment.signedURL}
                          className="preview-image"
                          alt="Preview"
                        />
                      ) : (
                        <i className={`fas preview-icon ${getFileIcon(attachment)}`}></i>
                      )}
                      {showProfilePicOption && isProfilePic(attachment) && (
                        <span className="profile-pic-badge" title="Profile Picture">
                          <i className="fas fa-user-circle"></i>
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <a role="button" className="title-link" onClick={() => openAttachmentUrl(attachment)}>
                      {attachment.title || attachment.filename}
                    </a>
                  </td>
                  <td className="cell-filename">{attachment.filename}</td>
                  <td>
                    <div className="created-info">
                      <span className="created-by">{attachment.createdBy}</span>
                      <span className="created-date">{formatDate(attachment.dateCreated)}</span>
                    </div>
                  </td>
                  <td>
                    <div className="actions">
                      {showProfilePicOption && hasImageExtension(attachment.filename) && isEditMode && (
                        <button
                          className={`btn-profile${isProfilePic(attachment) ? ' active' : ''}`}
                          onClick={() => setProfilePic(attachment.id)}
                          title={isProfilePic(attachment) ? 'Current profile picture' : 'Set as profile picture'}
                        >
                          <i className="fas fa-user-circle"></i>
                        </button>
                      )}
                      {isEditMode && (
                        <button
                          className="btn-delete"
                          onClick={() => openDeleteConfirm(attachment)}
                          title="Delete"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="mobile-cards">
        {attachmentCount === 0 && (
          <div className="empty-mobile">
            <i className="fas fa-paperclip"></i>
            No attachments found
          </div>
        )}
        {attachmentList.map((attachment) => (
          <div key={'mobile-' + attachment.id} className="card">
            <div className="card-preview" onClick={() => openAttachmentUrl(attachment)}>
              {hasImageExtension(attachment.filename) ? (
                <img
                  src={attachment.signedURL}
                  className="card-image"
                  alt="Preview"
                />
              ) : (
                <i className={`fas card-icon ${getFileIcon(attachment)}`}></i>
              )}
              {showProfilePicOption && isProfilePic(attachment) && (
                <span className="profile-pic-badge">
                  <i className="fas fa-user-circle"></i>
                </span>
              )}
            </div>
            <div className="card-content">
              <div className="card-title">
                <a role="button" onClick={() => openAttachmentUrl(attachment)}>
                  {attachment.title || attachment.filename}
                </a>
              </div>
              <div className="card-filename">{attachment.filename}</div>
              <div className="card-meta">
                <span>{attachment.createdBy}</span>
                <span>{formatDate(attachment.dateCreated)}</span>
              </div>
            </div>
            <div className="card-actions">
              {showProfilePicOption && hasImageExtension(attachment.filename) && isEditMode && (
                <button
                  className={`btn-profile${isProfilePic(attachment) ? ' active' : ''}`}
                  onClick={() => setProfilePic(attachment.id)}
                >
                  <i className="fas fa-user-circle"></i>
                </button>
              )}
              {isEditMode && (
                <button
                  className="btn-delete"
                  onClick={() => openDeleteConfirm(attachment)}
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={deleteConfirm.open}
        title="Delete Attachment?"
        confirmText="Delete"
        cancelText="Cancel"
        confirmClass="danger"
        theme={resolvedTheme}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      >
        <p>Are you sure you want to delete <strong>{deleteConfirm.attachment?.title || deleteConfirm.attachment?.filename || 'this attachment'}</strong>?</p>
      </ConfirmModal>
    </div>
  );
}

(window as any).NPSDAttachmentsReact = NPSDAttachments;
